import React, { useEffect, useRef, useState } from 'react';
import { createLocalAudioRecorder } from '../../../../../services/LocalAudioRecorder';
import './LocalRecordingView.scss';

/**
 * Gets a user-friendly error message based on the error type.
 * @param {Error} error - The error that occurred.
 * @returns {string} A user-friendly error message.
 */
function getErrorMessage(error) {
  if (error?.name === 'NotAllowedError' || error?.name === 'PermissionDeniedError') {
    return 'Microphone permission was denied. Please allow microphone access and try again.';
  }
  if (error?.name === 'NotFoundError' || error?.name === 'DevicesNotFoundError') {
    return 'No microphone found. Please connect a microphone and try again.';
  }
  if (error?.name === 'NotReadableError' || error?.name === 'TrackStartError') {
    return 'Microphone is in use by another application. Please close other apps using the microphone.';
  }
  return 'An error occurred while recording. Please try again.';
}

/**
 * LocalRecordingView component displays a recording interface with a green mic icon
 * and an animated progress bar that fills over 4 seconds while recording audio locally.
 * 
 * Uses CSS animation for the progress bar to avoid excessive re-renders.
 * Previous implementation used JavaScript intervals (50ms) causing ~80 renders per recording.
 * 
 * Handles microphone permission errors gracefully by displaying user-friendly messages
 * instead of crashing the component tree.
 * 
 * @param {Object} props - Component props.
 * @param {Function} props.onRecordingComplete - Callback when recording completes with blob URL.
 * @param {Function} props.onError - Callback when an error occurs during recording.
 */
export default function LocalRecordingView(props) {
  const { onRecordingComplete, onError } = props;
  
  const recorderRef = useRef(null);
  const isMountedRef = useRef(true); // Track if component is still mounted
  const [error, setError] = useState(null);

  // Use refs for callbacks to avoid the effect re-running when references change.
  // Without this, a new onRecordingComplete reference causes the effect to re-run,
  // which disposes the recorder mid-recording and calls getUserMedia again.
  const onRecordingCompleteRef = useRef(onRecordingComplete);
  onRecordingCompleteRef.current = onRecordingComplete;

  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useEffect(() => {
    isMountedRef.current = true;
    
    // Guard against double initialization in React Strict Mode
    if (recorderRef.current) return;

    /**
     * Initializes recording with proper error handling.
     * Catches microphone permission errors and other initialization failures.
     */
    const initRecording = async () => {
      try {
        const recorder = createLocalAudioRecorder();
        recorderRef.current = recorder;
        
        await recorder.startRecording({
          onComplete: (blobUrl) => {
            // Guard against callback firing after unmount (React Strict Mode)
            if (!isMountedRef.current) {
              URL.revokeObjectURL(blobUrl); // Clean up blob URL even if unmounted
              return;
            }
            
            // Small delay before transitioning to playback view (allows CSS animation to complete)
            setTimeout(() => {
              if (isMountedRef.current && onRecordingCompleteRef.current) {
                onRecordingCompleteRef.current(blobUrl);
              } else {
                URL.revokeObjectURL(blobUrl); // Clean up if unmounted during delay
              }
            }, 200);
          },
          onError: (recordingError) => {
            console.error('LocalRecordingView: Recording error', recordingError);
            // Guard against callback firing after unmount
            if (!isMountedRef.current) {
              return;
            }
            setError(recordingError);
            onErrorRef.current?.(recordingError);
          }
        });
      } catch (initError) {
        console.error('LocalRecordingView: Failed to initialize recording', initError);
        if (!isMountedRef.current) {
          return;
        }
        setError(initError);
        onErrorRef.current?.(initError);
      }
    };

    initRecording();

    // Cleanup on unmount
    return () => {
      isMountedRef.current = false;
      if (recorderRef.current) {
        recorderRef.current.dispose();
        recorderRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Show error state with user-friendly message
  if (error) {
    return (
      <div className="local-recording-view">
        <div className="local-recording-view__content">
          <div className="local-recording-view__error-icon">
            {/* Red warning icon */}
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              width="90" 
              height="88" 
              viewBox="0 0 45 44" 
              fill="none"
            >
              <path 
                d="M24.4886 1.69701C23.7101 0.225509 21.2891 0.225509 20.5106 1.69701L0.260634 39.947C0.0790338 40.2899 -0.0107944 40.674 -0.000125838 41.0619C0.0105427 41.4498 0.121345 41.8283 0.32152 42.1607C0.521695 42.4931 0.804438 42.7681 1.14228 42.959C1.48013 43.1499 1.8616 43.2501 2.24963 43.25H42.7496C43.1377 43.2508 43.5193 43.1511 43.8572 42.9605C44.1952 42.7699 44.4781 42.495 44.6782 42.1625C44.8783 41.8301 44.9888 41.4514 44.999 41.0636C45.0092 40.6757 44.9187 40.2918 44.7364 39.9493L24.4886 1.69701ZM24.7496 36.5H20.2496V32H24.7496V36.5ZM20.2496 27.5V16.25H24.7496L24.7519 27.5H20.2496Z" 
                fill="#E05858"
              />
            </svg>
          </div>
          
          <div className="local-recording-view__error-message">
            {getErrorMessage(error)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="local-recording-view">
      <div className="local-recording-view__content">
        <div className="local-recording-view__mic-container">
          {/* Green microphone icon */}
          <svg 
            className="local-recording-view__mic-icon"
            xmlns="http://www.w3.org/2000/svg" 
            width="100" 
            height="140" 
            viewBox="0 0 69 99" 
            fill="none"
          >
            <path 
              fillRule="evenodd" 
              clipRule="evenodd" 
              d="M34.5 69.1824C45.1361 69.1824 53.7857 60.3705 53.7857 49.546H42.6818C40.9873 49.546 39.6136 48.1723 39.6136 46.4778C39.6136 44.7833 40.9873 43.4096 42.6818 43.4096H53.7857V37.2733H42.6818C40.9873 37.2733 39.6136 35.8996 39.6136 34.2051C39.6136 32.5106 40.9873 31.1369 42.6818 31.1369H53.7857V25.0005H42.6818C40.9873 25.0005 39.6136 23.6269 39.6136 21.9324C39.6136 20.2378 40.9873 18.8642 42.6818 18.8642H53.7485C53.1245 8.6044 44.7313 0.455078 34.5 0.455078C24.2687 0.455078 15.8755 8.6044 15.2515 18.8642H26.3182C28.0127 18.8642 29.3864 20.2378 29.3864 21.9324C29.3864 23.6269 28.0127 25.0005 26.3182 25.0005H15.2143V31.1369H26.3182C28.0127 31.1369 29.3864 32.5106 29.3864 34.2051C29.3864 35.8996 28.0127 37.2733 26.3182 37.2733H15.2143V43.4096H26.3182C28.0127 43.4096 29.3864 44.7833 29.3864 46.4778C29.3864 48.1723 28.0127 49.546 26.3182 49.546H15.2143C15.2143 60.3705 23.8639 69.1824 34.5 69.1824ZM68.25 49.546V39.7278C68.25 38.4258 67.742 37.1772 66.8379 36.2566C65.9337 35.3359 64.7073 34.8187 63.4286 34.8187C62.1499 34.8187 60.9235 35.3359 60.0193 36.2566C59.1151 37.1772 58.6072 38.4258 58.6072 39.7278V49.546C58.6072 63.0804 47.7927 74.0914 34.5 74.0914C21.2073 74.0914 10.3929 63.0804 10.3929 49.546V39.7278C10.3929 38.4258 9.88489 37.1772 8.98069 36.2566C8.0765 35.3359 6.85015 34.8187 5.57143 34.8187C4.29271 34.8187 3.06636 35.3359 2.16216 36.2566C1.25797 37.1772 0.75 38.4258 0.75 39.7278V49.546C0.75 66.826 13.3484 81.1213 29.6786 83.5169V88.8187H15.2143C13.9356 88.8187 12.7092 89.3359 11.805 90.2566C10.9008 91.1772 10.3929 92.4258 10.3929 93.7278C10.3929 95.0298 10.9008 96.2784 11.805 97.1991C12.7092 98.1197 13.9356 98.6369 15.2143 98.6369H53.7857C55.0644 98.6369 56.2908 98.1197 57.195 97.1991C58.0992 96.2784 58.6072 95.0298 58.6072 93.7278C58.6072 92.4258 58.0992 91.1772 57.195 90.2566C56.2908 89.3359 55.0644 88.8187 53.7857 88.8187H39.3214V83.5169C55.6516 81.1213 68.25 66.826 68.25 49.546Z" 
              fill="#00A56F"
            />
          </svg>
        </div>
        
        <div className="local-recording-view__status">
          Recording...
        </div>
        
        <div className="local-recording-view__progress-container">
          <div className="local-recording-view__progress-bar" />
        </div>
      </div>
    </div>
  );
}
